/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Audio domain - Positional audio system and sound effect management
 * audio.client.ts - Client-side audio functions with distance-based volume calculations
 */

export interface AudioConfig {
  maxHearingRange: number;
  minVolume: number;
  maxVolume: number;
  falloffExponent: number;
}

export interface PlayerPosition {
  x: number;
  y: number;
}

export interface WorldDimensions {
  w: number;
  h: number;
}

const DEFAULT_AUDIO_CONFIG: AudioConfig = {
  maxHearingRange: 800, // Reduced range for more dramatic falloff
  minVolume: 0.005,
  maxVolume: 0.08,
  falloffExponent: 2.5, // Controls audio attenuation curve steepness
};

// Theme music singleton
let themeMusic: HTMLAudioElement | null = null;

// Pre-loaded audio files to avoid downloading on every play
const preloadedAudio: { [filename: string]: HTMLAudioElement } = {};
let audioInitialized = false;

/**
 * Pre-load all audio files to avoid downloading them repeatedly during gameplay
 * Call this once during game initialization
 */
export function initializeAudioSystem(): void {
  if (audioInitialized) return;

  const audioFiles = [
    "./assets/pickup.mp3",
    "./assets/pickup10.mp3",
    "./assets/pickup100.mp3",
    "./assets/laser1.mp3",
    "./assets/laser2.mp3",
    "./assets/laser3.mp3",
    "./assets/laser4.mp3",
    "./assets/laser5.mp3",
    "./assets/shot-impact.mp3",
  ];

  for (const filename of audioFiles) {
    const audio = new Audio(filename);
    audio.preload = "auto";
    preloadedAudio[filename] = audio;
  }

  audioInitialized = true;
}

/**
 * Reset audio system state (for testing)
 */
export function resetAudioSystem(): void {
  audioInitialized = false;
  Object.keys(preloadedAudio).forEach((key) => {
    delete preloadedAudio[key];
  });
}

/**
 * Disable auto-initialization for testing (sets initialized flag without loading audio)
 */
export function disableAudioInitialization(): void {
  audioInitialized = true;
  Object.keys(preloadedAudio).forEach((key) => {
    delete preloadedAudio[key];
  });
}

/**
 * Create a clone of a pre-loaded audio element for simultaneous playback
 * @param filename - Path to the audio file
 * @returns Cloned audio element ready for playback
 */
function cloneAudio(filename: string): HTMLAudioElement {
  if (!audioInitialized) {
    initializeAudioSystem();
  }

  const original = preloadedAudio[filename];
  if (!original) {
    return new Audio(filename); // Fallback: load without preloading if asset not found
  }

  const clone = original.cloneNode() as HTMLAudioElement;
  clone.volume = original.volume;
  return clone;
}

/**
 * Play pickup sound based on pellet value with appropriate audio file selection
 * @param pelletValue - Value of the collected pellet (determines sound pitch)
 */
export function playPickupSound(pelletValue: number): void {
  let audioFile = "./assets/pickup.mp3";

  if (pelletValue >= 100) {
    audioFile = "./assets/pickup100.mp3";
  } else if (pelletValue >= 10) {
    audioFile = "./assets/pickup10.mp3";
  }

  const sound = cloneAudio(audioFile);
  sound.volume = 0.3;
  sound.play().catch(() => {}); // Suppress autoplay policy errors
}

/**
 * Calculate wrapped distance between two points considering world boundaries
 * @param x1 - First point X coordinate
 * @param y1 - First point Y coordinate
 * @param x2 - Second point X coordinate
 * @param y2 - Second point Y coordinate
 * @param world - World dimensions for wrapping calculation
 * @returns Shortest distance considering world wrapping
 */
export function calculateWrappedDistance(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  world: WorldDimensions,
): number {
  const dx = Math.abs(x2 - x1);
  const dy = Math.abs(y2 - y1);

  const wrappedDx = Math.min(dx, world.w - dx);
  const wrappedDy = Math.min(dy, world.h - dy);

  return Math.sqrt(wrappedDx * wrappedDx + wrappedDy * wrappedDy);
}

/**
 * Calculate volume based on distance using exponential falloff curve
 * @param distance - Distance between sound source and listener
 * @param config - Audio configuration parameters
 * @returns Volume level (0 to 1)
 */
export function calculateVolumeFromDistance(
  distance: number,
  config: AudioConfig = DEFAULT_AUDIO_CONFIG,
): number {
  if (distance >= config.maxHearingRange) {
    return 0;
  }

  // Exponential falloff for more realistic audio attenuation
  const normalizedDistance = distance / config.maxHearingRange;
  const volumeRatio = (1 - normalizedDistance) ** config.falloffExponent;
  return config.minVolume + (config.maxVolume - config.minVolume) * volumeRatio;
}

/**
 * Play positional shooting sound with distance-based volume calculation
 * @param bulletX - X coordinate of bullet/sound source
 * @param bulletY - Y coordinate of bullet/sound source
 * @param playerPosition - Current player position (null if not available)
 * @param world - World dimensions for distance calculation
 * @param isCurrentPlayer - Whether this is the current player's shot (full volume)
 */
export function playPositionalShootSound(
  bulletX: number,
  bulletY: number,
  playerPosition: PlayerPosition | null,
  world: WorldDimensions,
  isCurrentPlayer: boolean = false,
): void {
  const laserSounds = [
    "./assets/laser1.mp3",
    "./assets/laser2.mp3",
    "./assets/laser3.mp3",
    "./assets/laser4.mp3",
    "./assets/laser5.mp3",
  ];
  const randomSound =
    laserSounds[Math.floor(Math.random() * laserSounds.length)];
  if (!randomSound) return;
  const sound = cloneAudio(randomSound);

  if (isCurrentPlayer) {
    sound.volume = 0.25;
  } else if (!playerPosition) {
    sound.volume = 0.1;
  } else {
    const distance = calculateWrappedDistance(
      bulletX,
      bulletY,
      playerPosition.x,
      playerPosition.y,
      world,
    );

    sound.volume = calculateVolumeFromDistance(distance);

    if (sound.volume === 0) {
      return;
    }
  }

  sound.play().catch(() => {}); // Suppress autoplay policy errors
}

/**
 * Start theme music with autoplay policy handling
 * Attempts to play background music, gracefully handles autoplay restrictions
 */
export function startThemeMusic(): void {
  if (!themeMusic) {
    themeMusic = document.getElementById("themeMusic") as HTMLAudioElement;
    if (!themeMusic) {
      return;
    }
  }

  if (themeMusic.paused) {
    themeMusic.volume = 0.3;
    themeMusic.play().catch(() => {}); // Suppress autoplay policy errors
  }
}

/**
 * Play impact sound for bullet-asteroid collisions
 * Uses medium volume for noticeable but not overwhelming impact effects
 */
export function playImpactSound(): void {
  const sound = cloneAudio("./assets/shot-impact.mp3");
  sound.volume = 0.4;
  sound.play().catch(() => {}); // Suppress autoplay policy errors
}

/**
 * Get current theme music element (for testing or advanced control)
 * @returns Current theme music HTML audio element or null
 */
export function getThemeMusic(): HTMLAudioElement | null {
  return themeMusic;
}

/**
 * Reset theme music reference (useful for testing)
 */
export function resetThemeMusic(): void {
  themeMusic = null;
}
